import { AppText } from "~/ui/app-text"
import { Link } from "~/ui/link"
import { brandAppName } from "~/common/brand"
import { getBrandWordmark } from "~/common/brand-assets"
import { getMetaContent } from "~/common/get-meta-content"

const supportEmail = getMetaContent<string>("SUPPORT_EMAIL")

type FAQItem = {
  question: string
  answer: React.ReactNode
}

const faqs: FAQItem[] = [
  {
    question: `What is ${brandAppName()}?`,
    answer: `${brandAppName()} is a platform where you can follow and participate in real conversations about topics you love. It's like having a front row seat to epic group chats, where amazing people discuss everything from sports to entertainment to finance.`,
  },
  {
    question: "How do I get started?",
    answer: `Download the ${brandAppName()} mobile app from the App Store or Google Play Store. Once installed, you can create an account using your phone number and start following conversations that interest you.`,
  },
  {
    question: "How can I contact support?",
    answer: (
      <span>
        For any questions or concerns, please email us at{" "}
        <a href={`mailto:${supportEmail}`} className="text-blue-600 hover:underline">
          {supportEmail}
        </a>
        . Our support team is available to help you Monday through Friday, 9 AM to 5 PM PST.
      </span>
    ),
  },
  {
    question: `Is ${brandAppName()} free?`,
    answer: `Yes, ${brandAppName()} is currently free to use. We may introduce premium features in the future, but we'll always maintain free options for our users.`,
  },
  {
    question: "How do I report inappropriate content?",
    answer: (
      <span>
        If you see content that violates our terms of service or community guidelines, please report
        it immediately by contacting us at{" "}
        <a href={`mailto:${supportEmail}`} className="text-blue-600 hover:underline">
          {supportEmail}
        </a>
        . We take all reports seriously and will investigate promptly.
      </span>
    ),
  },
  {
    question: `What devices can I use ${brandAppName()} on?`,
    answer: `${brandAppName()} is currently available on iOS and Android devices.`,
  },
  {
    question: "How do I delete my account?",
    answer: (
      <span>
        To delete your account, please email us at{" "}
        <a href={`mailto:${supportEmail}`} className="text-blue-600 hover:underline">
          {supportEmail}
        </a>
        . Please note that account deletion is permanent and cannot be undone.
      </span>
    ),
  },
  {
    question: "Is my data private and secure?",
    answer: (
      <span>
        We take your privacy and security seriously. Please review our{" "}
        <Link to="../privacy" className="text-blue-600 hover:underline">
          Privacy Policy
        </Link>{" "}
        to learn more about how we handle your data.
      </span>
    ),
  },
]

export const SupportScreen = () => {
  const wordmark = getBrandWordmark({
    ramble: "h-auto w-[240px] max-w-full",
    chatbcc: "h-[69px] w-[171px]",
  })
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-4xl px-4 py-16">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img
            src={wordmark.src}
            alt={`${wordmark.alt} Logo`}
            width={wordmark.width}
            height={wordmark.height}
            className={wordmark.className}
            loading="eager"
          />
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <AppText variant="h1" className="mb-4">
            Support & FAQ
          </AppText>
          <AppText variant="body1" className="text-neutral-600">
            Find answers to commonly asked questions about {brandAppName()}
          </AppText>
        </div>

        {/* FAQ Section */}
        <div className="space-y-8">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-neutral-200 pb-8">
              <AppText variant="h3" className="mb-4">
                {faq.question}
              </AppText>
              <AppText variant="body1" className="text-neutral-600">
                {faq.answer}
              </AppText>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-16 space-y-8 rounded-lg bg-neutral-50 p-8 text-center">
          <AppText variant="h3" className="mb-4">
            Still need help?
          </AppText>
          <AppText variant="body1" className="mb-6 text-neutral-600">
            Our support team is here to assist you with any questions or concerns.
          </AppText>
          <a
            href={`mailto:${supportEmail}`}
            className="inline-flex h-12 items-center justify-center rounded-full bg-[#2D92FF] px-8 text-white transition-colors hover:bg-[#2D92FF]/90"
          >
            <strong>Contact Support</strong>
          </a>
        </div>
      </div>
    </div>
  )
}
